import { reserveDemoLiveScheduling, releaseDemoLiveScheduling, drainLiveSaves } from '@/lib/demo/liveSchedulingReservation';
import { liveDemoSlot, syncDemoActiveSlot, setConfirmedDemoSession, setDemoEditsOpen, bindDemoCheckpoint, getBoundDemoCheckpoint, drainDemoDecisions, DEMOS_PROJECT, DEMOS_SPACE, type PersonaSlot } from '@/lib/demo/demoDecisionSession';
import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { useSandbox } from '@/contexts/SandboxContext';
import { useInstance } from '@/contexts/InstanceContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { DemoScenario, DemoStoryPoint } from '@/data/demoScenarios';
import { getDemoScenarioById } from '@/data/demoScenarios';
import type { AppRole } from '@/types/roles';
import { DemoTransitionCoordinator, type TransitionState, type PortContext } from '@/lib/demo/transitionCoordinator';
import { nextCheckpointPoll } from '@/lib/demo/checkpointPoll';
import { requestDemo, drainDemo, nextPaint, type DemoAck } from '@/lib/demo/demoBus';
import { worldAtStep, changedTasks, uiActionsOf, isCheckpointDecision, type DemoWorld } from '@/lib/demo/demoWorld';
import {
  resolveScenarioTargets, DEMOS_INSTANCE_ID, DemoPersistSession, checkpointSeq,
  hasUnrestorableSnapshot, createScenarioExact, captureScenarioSnapshot, restoreScenarioSnapshot, saveDurableSnapshot, loadDurableSnapshot, clearDurableSnapshot,
  type ScenarioSnapshot, type ScenarioTargets,
} from '@/lib/demo/demoPersistence';

const LIVE_TARGET = { instanceId: DEMOS_INSTANCE_ID, containerId: DEMOS_PROJECT };

export type AutoAdvanceSpeed = 'slow' | 'normal' | 'fast';
export const SPEED_MULTIPLIER: Record<AutoAdvanceSpeed, number> = { slow: 2, normal: 1, fast: 0.5 };
export const SPEED_MS: Record<AutoAdvanceSpeed, number> = { slow: 5000, normal: 3000, fast: 1500 };

export interface CheckpointView {
  target: string;
  approverName: string;
  expected: 'approved' | 'rejected';
  /** Saved status of the live review row, when known. */
  saved: string | null;
  /** Set after repeated failed reads; polling has stopped until Retry. */
  readError?: string | null;
}

interface DemoState {
  isDemoActive: boolean;
  isPreparing: boolean;
  /** Start failed before any walkthrough write; shown with Retry/Exit until dismissed. */
  startError: string | null;
  retryStart: () => void;
  /** Preparation Exit could not restore; Retry/Exit retry the restore, or leave without restoring. */
  prepRestoreFailed: boolean;
  /** Chapter a failed change was moving to (index), null otherwise. */
  failedChapterIndex: number | null;
  /** Last chapter whose change fully completed. */
  committedChapterIndex: number;
  demoRecentlyEnded: boolean;
  showIntro: boolean;
  dismissIntro: () => void;
  currentScenario: DemoScenario | null;
  currentStepIndex: number;
  currentStep: DemoStoryPoint | null;
  totalSteps: number;
  isExecutingAction: boolean;
  isPaused: boolean;
  isAutoAdvancing: boolean;
  autoAdvanceSpeed: AutoAdvanceSpeed;
  demoRuntimeMinutes: number;
  currentIntervalMs: number;
  transitionError: string | null;
  transitionFailedOp: 'goto' | 'exit' | null;
  /** Set while a chapter waits for the presenter's real in-app decision. */
  checkpoint: CheckpointView | null;
  startDemo: (scenarioId: string, runtimeMinutes?: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  exitDemo: () => void;
  forceExitDemo: () => void;
  retryTransition: () => void;
  retryCheckpointRead: () => void;
  togglePause: () => void;
  jumpToStep: (index: number) => void;
  toggleAutoAdvance: () => void;
  setAutoAdvanceSpeed: (speed: AutoAdvanceSpeed) => void;
}

const noop = () => {};
const DemoContext = createContext<DemoState>({
  isDemoActive: false, isPreparing: false, startError: null, retryStart: noop, prepRestoreFailed: false, failedChapterIndex: null, committedChapterIndex: 0, demoRecentlyEnded: false, showIntro: false, dismissIntro: noop,
  currentScenario: null, currentStepIndex: 0, currentStep: null, totalSteps: 0, isExecutingAction: false,
  isPaused: false, isAutoAdvancing: false, autoAdvanceSpeed: 'normal', demoRuntimeMinutes: 10,
  currentIntervalMs: 3000, transitionError: null, transitionFailedOp: null, checkpoint: null,
  startDemo: noop, nextStep: noop, prevStep: noop, exitDemo: noop, forceExitDemo: noop, retryTransition: noop, retryCheckpointRead: noop,
  togglePause: noop, jumpToStep: noop, toggleAutoAdvance: noop, setAutoAdvanceSpeed: noop,
});

interface PriorView { sandbox: { role: AppRole; userId: string; label: string } | null; view: Record<string, unknown> | null }

/** Leaf tasks' expected progress, for confirming the app shows saved data. */
function expectedLeafProgress(sc: DemoScenario, world: DemoWorld, targets: ScenarioTargets): Record<string, number> {
  const out: Record<string, number> = {};
  for (const t of sc.tasks) {
    if (sc.tasks.some(c => c.parentName === t.name)) continue;
    const p = world.tasks[t.name].progress;
    out[targets.taskIds[t.name]] = p === 100 ? 100 : p;
  }
  return out;
}

export function DemoProvider({ children }: { children: ReactNode }) {
  const sandbox = useSandbox();
  const { enterSandbox, exitSandbox } = sandbox;
  const { currentInstanceId } = useInstance();
  const [scenario, setScenario] = useState<DemoScenario | null>(null);
  const [ts, setTs] = useState<TransitionState | null>(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [demoRecentlyEnded, setDemoRecentlyEnded] = useState(false);
  const recentlyEndedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [autoAdvanceSpeed, setAutoAdvanceSpeedState] = useState<AutoAdvanceSpeed>('normal');
  const [runtimeMinutes, setRuntimeMinutes] = useState(10);
  const [showIntro, setShowIntro] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const lastStartRef = useRef<{ id: string; mins?: number } | null>(null);
  const retryingRef = useRef(false);
  const [checkpoint, setCheckpoint] = useState<CheckpointView | null>(null);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const coordRef = useRef<DemoTransitionCoordinator | null>(null);
  const actorSlotRef = useRef<string | null>(null);
  const snapshotRef = useRef<ScenarioSnapshot | null>(null);
  const targetsRef = useRef<ScenarioTargets | null>(null);
  const sessionRef = useRef<DemoPersistSession | null>(null);
  const liveRef = useRef<Set<number>>(new Set());
  const cpSeqRef = useRef<number | null>(null);
  const priorRef = useRef<PriorView | null>(null);
  const startAbortRef = useRef<AbortController | null>(null);
  // Automatic background live scheduling is suspended for the Demos instance
  // from start preparation until the run fully ends (see liveSchedulingReservation).
  const liveReservationRef = useRef<symbol | null>(null);
  /** The running start/preparation work; every end path joins it first. */
  const startWorkRef = useRef<Promise<void> | null>(null);
  /** Preparation Exit could not restore: snapshot + reservation kept for Retry/Exit. */
  const prepRecoveryRef = useRef(false);
  const [prepRestoreFailed, setPrepRestoreFailed] = useState(false);
  const sandboxRef = useRef(sandbox);
  sandboxRef.current = sandbox;

  const isDemoActive = scenario !== null;
  const totalSteps = scenario?.storyPoints.length ?? 0;
  const committed = ts?.committedIndex ?? -1;
  // A failed chapter change stays labelled as the chapter it was moving to
  // (the screen may already partly show it); the committed chapter is kept separately.
  const failedTarget = ts?.status === 'error' && ts.failedOp === 'goto' && ts.targetIndex != null ? ts.targetIndex : null;
  const stepIndex = Math.max(0, ts?.status === 'transitioning' && ts.targetIndex != null ? ts.targetIndex : failedTarget ?? committed);
  const currentStep = scenario?.storyPoints[stepIndex] ?? null;
  const busy = isPreparing || ts?.status === 'transitioning' || ts?.status === 'exiting';
  const baseIntervalMs = totalSteps > 0 ? (runtimeMinutes * 60 * 1000) / totalSteps : 3000;
  const currentIntervalMs = baseIntervalMs * SPEED_MULTIPLIER[autoAdvanceSpeed];

  const dismissIntro = useCallback(() => setShowIntro(false), []);

  const restorePriorView = useCallback(async () => {
    const prior = priorRef.current;
    priorRef.current = null;
    if (prior?.sandbox) enterSandbox(prior.sandbox.role, prior.sandbox.userId, prior.sandbox.label);
    else exitSandbox();
    if (prior?.view) await requestDemo('restore-view', prior.view);
  }, [enterSandbox, exitSandbox]);

  const endSession = useCallback(async (message: string | null) => {
    // This run's OWN reservation is held through restorePriorView and released
    // only after it completes; a newer run's token is never released here.
    const ownReservation = liveReservationRef.current;
    prepRecoveryRef.current = false;
    setPrepRestoreFailed(false);
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    startAbortRef.current?.abort();
    setConfirmedDemoSession(null);
    coordRef.current = null;
    actorSlotRef.current = null;
    snapshotRef.current = null;
    targetsRef.current = null;
    sessionRef.current = null;
    liveRef.current = new Set();
    setCheckpoint(null);
    setScenario(null);
    setTs(null);
    setIsPreparing(false);
    setIsPaused(false);
    setIsAutoAdvancing(false);
    setShowIntro(false);
    setStartError(null);
    delete (window as unknown as Record<string, unknown>).__workitect_demo_active;
    try {
      await restorePriorView();
    } finally {
      releaseDemoLiveScheduling(ownReservation);
      if (liveReservationRef.current === ownReservation) liveReservationRef.current = null;
    }
    setDemoRecentlyEnded(true);
    if (recentlyEndedTimerRef.current) clearTimeout(recentlyEndedTimerRef.current);
    recentlyEndedTimerRef.current = setTimeout(() => setDemoRecentlyEnded(false), 15000);
    if (message) toast.info(message);
  }, [restorePriorView]);

  const buildCoordinator = useCallback((sc: DemoScenario, targets: ScenarioTargets, session: DemoPersistSession) => {
    const baseMs = Date.now() - 60 * 60_000;
    const opt = (ctx: PortContext) => ({ signal: ctx.signal, sessionId: sessionIdOf() });
    let coordId = '';
    const sessionIdOf = () => coordId;
    const expectAt = (index: number): { expect: Record<string, number> } | { reason: string } => {
      try {
        const world = worldAtStep(sc, index, baseMs, {
          preDecision: isCp(index) && !liveRef.current.has(index),
          liveCheckpoints: liveRef.current,
        });
        return { expect: expectedLeafProgress(sc, world, targets) };
      } catch (e) { return { reason: (e as Error).message }; }
    };
    const isCp = (i: number) => !!sc.storyPoints[i]?.checkpoint;

    const coord = new DemoTransitionCoordinator({
      // In-flight review decisions settle before any baseline/restore write.
      drain: async () => { await drainDemoDecisions(); await drainDemo(); },
      clearUi: (ctx) => requestDemo('clear-ui', {}, opt(ctx)),
      resetBaseline: async (ctx) => {
        const snap = snapshotRef.current;
        if (!snap) return { ok: false, reason: 'No pre-walkthrough snapshot — refusing to reset.' };
        session.reset();
        liveRef.current = new Set();
        return restoreScenarioSnapshot(snap, ctx.isCurrent);
      },
      persistChapter: async (index, fromIndex, ctx) => {
        let world: DemoWorld, prev: DemoWorld | null;
        try {
          world = worldAtStep(sc, index, baseMs, { preDecision: isCp(index), liveCheckpoints: liveRef.current });
          prev = fromIndex == null ? null : worldAtStep(sc, fromIndex, baseMs, { liveCheckpoints: liveRef.current });
        } catch (e) {
          return { ok: false, reason: `Script error: ${(e as Error).message}` };
        }
        let live: { task: string; seq: number } | null = null;
        const cp = sc.storyPoints[index].checkpoint;
        if (cp) {
          const seq = checkpointSeq(world, cp.target, cp.approverSlot);
          if (seq == null) return { ok: false, reason: `Script error: no open review for ${cp.approverSlot} on "${cp.target}".` };
          cpSeqRef.current = seq;
          live = { task: cp.target, seq };
        } else {
          cpSeqRef.current = null;
        }
        const names = prev ? changedTasks(prev, world) : Object.keys(world.tasks);
        return session.persistWorld(world, names, baseMs, ctx.isCurrent, live);
      },
      refetch: (index, ctx) => {
        const ex = expectAt(index);
        if ('reason' in ex) return Promise.resolve({ ok: false, reason: ex.reason });
        return requestDemo('refetch', { expect: ex.expect }, { ...opt(ctx), timeoutMs: 20_000 });
      },
      switchActor: async (index, ctx) => {
        const step = sc.storyPoints[index];
        const persona = sc.personas[step.actAsSlot];
        if (!persona) return { ok: false, reason: `Unknown persona ${step.actAsSlot}` };
        const s = sandboxRef.current;
        if (!(s.isActive && s.sandboxSlotKey === persona.slotKey && s.impersonatedLabel === persona.name)) {
          enterSandbox(persona.role as AppRole, persona.slotKey, persona.name);
        }
        await nextPaint();
        const ex = expectAt(index);
        if ('reason' in ex) return { ok: false, reason: ex.reason };
        // The actor switch reloads tasks; the host confirms identity, exact
        // project scope AND that every manifest task is hydrated at its saved
        // progress after that reload before this acknowledges.
        const ack = await requestDemo('actor-view', {
          role: persona.role, slotKey: persona.slotKey, name: persona.name,
          projectId: targets.projectId, taskIds: Object.values(targets.taskIds), expect: ex.expect,
        }, { ...opt(ctx), timeoutMs: 20_000 });
        if (ack.ok) actorSlotRef.current = persona.slotKey; // only once confirmed
        return ack;
      },
      applyUi: async (index, ctx) => {
        const step = sc.storyPoints[index];
        for (const action of uiActionsOf(step)) {
          if (!ctx.isCurrent()) return { ok: false, reason: 'superseded' };
          if (action.type === 'trigger-notification') { toast.info(action.value ?? ''); continue; }
          const taskId = action.target ? targets.taskIds[action.target] : undefined;
          const payload = action.type === 'trigger-checkin'
            ? { action, taskId, assigneeSlot: step.actAsSlot, scenarioName: sc.name }
            : { action, taskId };
          const ack: DemoAck = await requestDemo('ui', payload, opt(ctx));
          if (!ack.ok) return ack;
        }
        return { ok: true };
      },
      isCheckpoint: isCp,
      finalizeCheckpoint: async (index, ctx) => {
        liveRef.current.add(index);
        let pre: DemoWorld, post: DemoWorld;
        try {
          pre = worldAtStep(sc, index, baseMs, { preDecision: true, liveCheckpoints: liveRef.current });
          post = worldAtStep(sc, index, baseMs, { liveCheckpoints: liveRef.current });
        } catch (e) { return { ok: false, reason: (e as Error).message }; }
        return session.persistWorld(post, changedTasks(pre, post), baseMs, ctx.isCurrent);
      },
      restore: async () => {
        await requestDemo('clear-ui', {});
        const snap = snapshotRef.current;
        if (!snap) return { ok: false, reason: 'No pre-walkthrough snapshot.' };
        const ack = await restoreScenarioSnapshot(snap);
        if (!ack.ok) return ack;
        clearDurableSnapshot(sc.id);
        return requestDemo('refetch', { expect: Object.fromEntries(snap.tasks.map(t => [t.id, t.progress])) }, 20_000);
      },
    }, sc.storyPoints.length);
    coordId = coord.snapshot.sessionId;
    return coord;
  }, [enterSandbox]);

  const startDemo = useCallback((scenarioId: string, runtimeMins?: number) => {
    lastStartRef.current = { id: scenarioId, mins: runtimeMins };
    setStartError(null);
    if (coordRef.current || startAbortRef.current || prepRecoveryRef.current) return;
    if (currentInstanceId !== DEMOS_INSTANCE_ID) {
      toast.error('Walkthroughs only run in the Demos workspace.', { description: 'Switch to Demos to start one.' });
      return;
    }
    const sc = getDemoScenarioById(scenarioId);
    if (!sc) { toast.error('Walkthrough not found.'); return; }

    const abort = new AbortController();
    startAbortRef.current = abort;
    // Synchronous, before any await or snapshot: no new automatic scheduling
    // save can be admitted for the Demos instance from here on.
    const prevReservation = liveReservationRef.current;
    liveReservationRef.current = reserveDemoLiveScheduling(DEMOS_INSTANCE_ID, DEMOS_PROJECT);
    releaseDemoLiveScheduling(prevReservation);
    const s = sandboxRef.current;
    const keepPrior = retryingRef.current && !!priorRef.current;
    if (!keepPrior) priorRef.current = {
      sandbox: s.isActive && s.impersonatedRole && s.impersonatedUserId
        ? { role: s.impersonatedRole, userId: s.impersonatedUserId, label: s.impersonatedLabel ?? '' } : null,
      view: null,
    };
    setScenario(sc);
    setIsPreparing(true);
    setIsPaused(false);
    setIsAutoAdvancing(false);
    setShowIntro(true);
    setCheckpoint(null);
    setRuntimeMinutes(runtimeMins ?? sc.runtimeMinutes ?? 10);
    (window as unknown as Record<string, unknown>).__workitect_demo_active = true;

    const fail = (message: string) => {
      if (startAbortRef.current !== abort) return;
      startAbortRef.current = null;
      // A start failure happens before any coordinator exists: never leave a
      // confirmed session (reschedule refusal / undo block) behind it.
      if (!coordRef.current) setConfirmedDemoSession(null);
      // Keep the intro visible with the reason; the presenter chooses Retry or Exit.
      setIsPreparing(false);
      setStartError(message);
    };

    /** Dispatch the legacy start step and await its ready event (listener/timeout always cleaned up). */
    const runStartProject = () => new Promise<{ success: boolean; spaceId: string | null; reason?: string | null }>((resolve) => {
      const cleanup = () => { window.removeEventListener('demo:start-ready', onReady); clearTimeout(timer); abort.signal.removeEventListener('abort', onAbort); };
      const onReady = (e: Event) => {
        const d = (e as CustomEvent<{ success?: boolean; reason?: string | null; scenarioId?: string | null; spaceId?: string | null }>).detail ?? {};
        if (d.scenarioId && d.scenarioId !== sc.id) return;
        cleanup();
        resolve({ success: !!d.success, spaceId: d.spaceId ?? null, reason: d.reason ?? null });
      };
      const onAbort = () => { cleanup(); resolve({ success: false, spaceId: null }); };
      const timer = setTimeout(() => { cleanup(); resolve({ success: false, spaceId: null, reason: 'The Demos workspace did not respond in time.' }); }, 60_000);
      window.addEventListener('demo:start-ready', onReady);
      abort.signal.addEventListener('abort', onAbort);
      window.dispatchEvent(new CustomEvent('demo:start-project', { detail: { scenario: sc } }));
    });

    const work = (async () => {
      await drainDemo(); // never overlap an old session's work
      await drainLiveSaves(LIVE_TARGET); // already-launched Demos automatic saves settle before any read/restore/snapshot
      if (abort.signal.aborted) return;
      const view = await requestDemo<Record<string, never>, DemoAck & { view?: Record<string, unknown> }>('capture-view', {});
      if (abort.signal.aborted) return;
      if (priorRef.current && !keepPrior) priorRef.current.view = (view as { view?: Record<string, unknown> }).view ?? null;

      // 1. Hydrate the fixed Demos space + project (waits for host data; no writes).
      const ready = await runStartProject();
      if (abort.signal.aborted) return;
      if (!ready.success) { fail(ready.reason ?? 'The walkthrough could not be prepared.'); return; }
      const spaceId = ready.spaceId;
      if (spaceId !== DEMOS_SPACE) { fail('The Demos walkthrough space was not found; nothing was changed.'); return; }
      if (hasUnrestorableSnapshot(sc.id)) {
        fail('Restoration data from an older walkthrough version is still in this browser and cannot be restored exactly. Nothing was changed; ask for a manual check before starting again.');
        return;
      }
      // 2. Durable restoration material from an interrupted session comes first.
      const leftover = loadDurableSnapshot(sc.id);
      if (leftover) {
        const ack = await restoreScenarioSnapshot(leftover, () => !abort.signal.aborted);
        if (!ack.ok) { fail(`An earlier walkthrough left changes that could not be undone: ${ack.reason}`); return; }
        clearDurableSnapshot(sc.id);
        toast.info('Put back changes left by an earlier, interrupted walkthrough.');
      }

      // 3. Resolve exact targets BEFORE any write; create once only if missing.
      let r = await resolveScenarioTargets(sc, spaceId, currentInstanceId);
      if (abort.signal.aborted) return;
      if (r.ok === false && !r.missing) { fail(r.reason); return; }
      if (r.ok === false) {
        const created = await createScenarioExact(sc, currentInstanceId, abort.signal);
        if (!created.ok) { if (!abort.signal.aborted) fail(`Could not create the sample project: ${created.reason}`); return; }
        await requestDemo('refetch', {}, 20_000);
        r = await resolveScenarioTargets(sc, spaceId, currentInstanceId);
        if (r.ok === false) { fail(r.reason); return; }
      }
      const targets = r.targets;
      targetsRef.current = targets;
      if (targets.projectId !== DEMOS_PROJECT || targets.spaceId !== DEMOS_SPACE) { fail('The scenario is not in the fixed Demos project and space; nothing was changed.'); return; }

      await drainLiveSaves(LIVE_TARGET); // none can be admitted now; defensive before capture
      if (abort.signal.aborted) return;
      const snap = await captureScenarioSnapshot(sc, targets);
      // Capture is read-only: an Exit during it leaves nothing to restore and must not install anything.
      if (abort.signal.aborted) return;
      if (snap.ok === false) { fail(snap.reason); return; }
      if (!saveDurableSnapshot(snap.snapshot)) { fail('Could not store restoration data in this browser — walkthrough not started.'); return; }
      snapshotRef.current = snap.snapshot;

      const { data: auth } = await supabase.auth.getUser();
      if (abort.signal.aborted) return; // late auth after Exit never confirms a session
      if (!auth?.user?.id) { fail('Sign in to run a walkthrough.'); return; }
      setConfirmedDemoSession({
        sessionKey: `${sc.id}:${snap.snapshot.capturedAt}`, instanceId: targets.instanceId,
        projectId: targets.projectId, spaceId: targets.spaceId, taskIds: new Set(Object.values(targets.taskIds)),
      });

      const session = new DemoPersistSession(sc, targets, auth.user.id);
      sessionRef.current = session;
      const coord = buildCoordinator(sc, targets, session);
      coordRef.current = coord;
      coord.subscribe((st) => {
        // Withdraw review eligibility synchronously the moment a transition/exit starts.
        if (st.status !== 'awaiting' && getBoundDemoCheckpoint()) bindDemoCheckpoint(null);
        // Interactive scenario-task edits only while resting on a chapter.
        setDemoEditsOpen(st.status === 'idle' || st.status === 'awaiting');
        setTs(st);
      });
      startAbortRef.current = null;
      setIsPreparing(false);
      void coord.goTo(0);
    })().catch((e: unknown) => { fail(`The walkthrough could not be prepared: ${(e as Error)?.message ?? 'unknown error'}`); });
    startWorkRef.current = work;
    void work.finally(() => { if (startWorkRef.current === work) startWorkRef.current = null; });
  }, [currentInstanceId, buildCoordinator]);

  // Instance switch or unmount mid-session: stop all work.
  useEffect(() => {
    if (!scenario) return;
    if (currentInstanceId !== DEMOS_INSTANCE_ID) {
      startAbortRef.current?.abort();
      startAbortRef.current = null;
      coordRef.current?.abandon();
      toast.error('Walkthrough stopped because the workspace changed. Return to Demos and start it again to put the sample project back.');
      // Abandon (durable restore copy kept): join preparation so nothing late installs a session.
      const pending = startWorkRef.current;
      void (async () => { await pending; await endSession(null); })();
    }
  }, [currentInstanceId, scenario, endSession]);
  useEffect(() => () => {
    startAbortRef.current?.abort();
    coordRef.current?.abandon();
    // Unmount abandons (durable restore copy kept); the aborted preparation cannot confirm a session.
    releaseDemoLiveScheduling(liveReservationRef.current);
    liveReservationRef.current = null;
  }, []);

  // Interactive checkpoint: confirm the presenter's decision from saved data.
  // Review eligibility follows the persona actually shown in Sandbox (Return to
  // my account or a manual persona switch re-binds immediately).
  const liveSlot = liveDemoSlot(sandbox, scenario?.personas as Partial<Record<string, { name: string }>> | undefined);
  const liveSlotRef = useRef(liveSlot);
  liveSlotRef.current = liveSlot;
  useEffect(() => { syncDemoActiveSlot(liveSlot); }, [liveSlot]);

  const [cpReadAttempt, setCpReadAttempt] = useState(0);
  const retryCheckpointRead = useCallback(() => setCpReadAttempt(n => n + 1), []);

  useEffect(() => {
    const sc = scenario;
    if (!sc || ts?.status !== 'awaiting') { setCheckpoint(null); return; }
    const step = sc.storyPoints[ts.committedIndex];
    const cp = step?.checkpoint;
    const seq = cpSeqRef.current;
    if (!cp || seq == null) return;
    const decision = (step.autoActions ?? []).find(a => isCheckpointDecision(step, a));
    const expected = decision?.type === 'reject-task' ? 'rejected' : 'approved';
    const approverName = sc.personas[cp.approverSlot]?.name ?? cp.approverSlot;
    setCheckpoint({ target: cp.target, approverName, expected, saved: 'pending', readError: null });
    const cpTaskId = sessionRef.current ? targetsRef.current?.taskIds[cp.target] : undefined;
    if (cpTaskId) bindDemoCheckpoint({ taskId: cpTaskId, reviewerSlot: cp.approverSlot as PersonaSlot, activeSlot: liveSlotRef.current });
    let stopped = false;
    let failures = 0;
    const tick = async () => {
      const session = sessionRef.current;
      const coord = coordRef.current;
      if (stopped || !session || !coord) return;
      const res = await session.liveDecision(cp.target, seq);
      if (stopped) return;
      if (res.ok) setCheckpoint(c => (c ? { ...c, saved: res.status, readError: null } : c));
      const next = nextCheckpointPoll(failures, res, expected);
      failures = next.failures;
      if (next.action === 'confirm') { stopped = true; void coord.confirmCheckpoint(); return; }
      if (next.action === 'error') {
        // Bounded: stop polling; the presenter chooses Retry (restarts reads) or Exit.
        stopped = true;
        setCheckpoint(c => (c ? { ...c, readError: next.message } : c));
        return;
      }
      timer = setTimeout(tick, 1500);
    };
    let timer = setTimeout(tick, 800);
    return () => { stopped = true; clearTimeout(timer); };
  }, [scenario, ts?.status, ts?.committedIndex, cpReadAttempt]);

  const jumpToStep = useCallback((index: number) => {
    const c = coordRef.current;
    if (!c || c.busy || isPreparing) return;
    void c.goTo(index);
  }, [isPreparing]);

  const nextStep = useCallback(() => {
    const c = coordRef.current;
    // After a failed change, Next/Back are ambiguous: require Retry, Exit or an explicit jump.
    if (c && c.snapshot.status !== 'error') jumpToStep(c.snapshot.committedIndex + 1);
  }, [jumpToStep]);

  const prevStep = useCallback(() => {
    const c = coordRef.current;
    if (c && c.snapshot.status !== 'error' && c.snapshot.committedIndex > 0) jumpToStep(c.snapshot.committedIndex - 1);
  }, [jumpToStep]);

  // Serializes every exit path: a second Exit joins the one in flight.
  const exitInFlightRef = useRef<Promise<void> | null>(null);
  const exitDemo = useCallback((): Promise<void> => {
    if (exitInFlightRef.current) return exitInFlightRef.current;
    const p = exitDemoOnce().finally(() => { exitInFlightRef.current = null; });
    exitInFlightRef.current = p;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endSession, scenario]);
  async function exitDemoOnce() {
    const c = coordRef.current;
    if (!c) {
      // Preparing, failed start, or failed preparation restore: stop and JOIN
      // the preparation work first, then put back anything already captured.
      startAbortRef.current?.abort();
      startAbortRef.current = null;
      await startWorkRef.current;
      await drainDemo();
      await drainLiveSaves(LIVE_TARGET);
      const snap = snapshotRef.current;
      if (snap) {
        const ack = await restoreScenarioSnapshot(snap);
        if (!ack.ok) {
          // Keep snapshot + reservation; no false ended state. Retry/Exit retries, or leave without restoring.
          prepRecoveryRef.current = true;
          setPrepRestoreFailed(true);
          setIsPreparing(false);
          setStartError(`The sample project could not be put back: ${ack.reason ?? 'restore failed'}. Retry, or leave without restoring.`);
          return;
        }
        clearDurableSnapshot(scenario?.id ?? lastStartRef.current?.id ?? '');
      }
      await endSession(null);
      return;
    }
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    setIsAutoAdvancing(false);
    const ack = await c.exit();
    if (ack.ok) await endSession('Walkthrough ended — the sample project is back as it was.');
    else if (ack.reason !== 'superseded') toast.error('The sample project could not be restored.', { description: 'Retry, or leave without restoring.' });
  }

  const retryTransition = useCallback(() => {
    const c = coordRef.current;
    if (!c || c.busy) return;
    if (c.snapshot.failedOp === 'exit') { void exitDemo(); return; }
    void c.retry();
  }, [exitDemo]);

  const forceExitDemo = useCallback(() => {
    // Explicit abandonment: nothing is restored now; the durable restore copy
    // stays in this browser and is put back on the next start.
    startAbortRef.current?.abort();
    startAbortRef.current = null;
    coordRef.current?.abandon();
    const pending = startWorkRef.current;
    void (async () => { await pending; await endSession('Walkthrough closed without restoring. Start this walkthrough again to put the sample project back.'); })();
  }, [endSession]);

  const togglePause = useCallback(() => setIsPaused(p => !p), []);
  const toggleAutoAdvance = useCallback(() => setIsAutoAdvancing(a => !a), []);
  const setAutoAdvanceSpeed = useCallback((sp: AutoAdvanceSpeed) => setAutoAdvanceSpeedState(sp), []);

  useEffect(() => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    if (!isAutoAdvancing || isPaused || busy || showIntro || !scenario || ts?.status !== 'idle') return;
    if (committed >= totalSteps - 1) { setIsAutoAdvancing(false); return; }
    autoTimerRef.current = setTimeout(() => nextStep(), currentIntervalMs);
    return () => { if (autoTimerRef.current) clearTimeout(autoTimerRef.current); };
  }, [isAutoAdvancing, isPaused, busy, showIntro, scenario, ts?.status, committed, totalSteps, currentIntervalMs, nextStep]);

  return (
    <DemoContext.Provider value={{
      isDemoActive, isPreparing, failedChapterIndex: failedTarget, committedChapterIndex: Math.max(0, committed), startError, prepRestoreFailed, retryStart: () => { if (prepRecoveryRef.current) { void exitDemo(); return; } const l = lastStartRef.current; if (l) { retryingRef.current = true; startDemo(l.id, l.mins); retryingRef.current = false; } }, demoRecentlyEnded, showIntro, dismissIntro,
      currentScenario: scenario, currentStepIndex: stepIndex, currentStep, totalSteps,
      isExecutingAction: !!busy, isPaused, isAutoAdvancing, autoAdvanceSpeed,
      demoRuntimeMinutes: runtimeMinutes, currentIntervalMs,
      transitionError: ts?.status === 'error' ? ts.error : null,
      transitionFailedOp: ts?.status === 'error' ? ts.failedOp : null,
      checkpoint,
      startDemo, nextStep, prevStep, exitDemo: () => { void exitDemo(); }, forceExitDemo, retryTransition, retryCheckpointRead,
      togglePause, jumpToStep, toggleAutoAdvance, setAutoAdvanceSpeed,
    }}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  return useContext(DemoContext);
}
